import { ccfRoutes } from '@app/ccf/ccf-routes';
import { weightOptions } from '@app/ccf/options/weight-options';
import { WeightOptionEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideTemplate from '@shared/components/guide-template';
import { FormLabel } from '@shared/components/ui/form';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom';
import { useCCF } from '../../ccf-context';

export default function DistributionFlowAddWeightPage() {
  const { handleSubmit, trigger } = useFormContext();
  const navigate = useNavigate();
  const { uuid: reportUuid } = useCCF();

  const context: any = useOutletContext();

  const { onSubmit, isCreating, updateMode, flowUuid } = context;
  const { watch } = useFormContext();

  const selectedWeightOption = watch('weightOption');

  const onCompleteFlow = async () => {
    const isValid = await trigger();
    isValid && handleSubmit(onSubmit)();
  };

  return (
    <GuideTemplate
      preTitle="ADD DISTRIBUTION FLOW"
      title="Add transported weight"
      description={
        <Text>
          The final step is to estimate how much of your product, by weight, is
          distributed via this flow. We recommend estimating using a % of your
          total products sold, which we will base on the total weight of your
          purchased goods.
        </Text>
      }
      primaryActionText={updateMode ? 'Update flow' : 'Complete flow'}
      primaryActionProps={{
        onClick: onCompleteFlow,
        type: 'submit',
        loading: isCreating,
        disabled: isCreating,
      }}
      secondaryActionText="Back"
      secondaryActionProps={{
        onClick: () =>
          navigate(
            generatePath(ccfRoutes.DISTRIBUTION_FLOW_REVIEW, {
              report_uuid: reportUuid,
              flow_uuid: flowUuid || '',
            })
          ),
        disabled: isCreating,
      }}
    >
      <form>
        <fieldset
          disabled={isCreating}
          className="flex w-full flex-col gap-6 text-left"
        >
          <div>
            <FormLabel className="text-left">
              How much of your product do you distribute via this flow?
            </FormLabel>
            <div className="mt-2 flex items-center gap-2">
              <InputFormField
                name="weight"
                type="number"
                className="w-32"
                min={1}
                max={
                  selectedWeightOption !== WeightOptionEnum['total_weight']
                    ? 100
                    : undefined
                }
              />
              <SelectFormField
                name="weightOption"
                options={weightOptions}
                placeholder="Select unit"
                className="w-full"
              />
            </div>{' '}
          </div>
          <div>
            <FormLabel className="text-left">
              What percentage of this flow requires cold-chain distribution?
            </FormLabel>
            <InputFormField
              name="coldChainPercentage"
              type="number"
              min={1}
              max={100}
              step={0.1}
              className="mt-2 w-24"
              adornmentRight="%"
            />
          </div>
        </fieldset>
      </form>
    </GuideTemplate>
  );
}
